import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Input } from "../../../../../common/Input";
import { CheckInput } from "../../../../../common/Input";
import { ConfirmationModal } from "../../../../../common/Modal";

const HRCalendarAddEditModal = ({ isOpen, onClose, selectedItem, onSave, existingData, currentYearData }) => {
  const [form, setForm] = useState({
    year: "",
    isActive: true,
    isCurrentYear: false,
  });

  const [errors, setErrors] = useState({
    year: "",
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        setForm({
          year: selectedItem.year.toString(),
          isActive: selectedItem.isActive,
          isCurrentYear: selectedItem.isCurrentYear,
        });
      } else {
        // Default to next year for new entries
        const nextYear = (new Date().getFullYear() + 1).toString();
        setForm({
          year: nextYear,
          isActive: true,
          isCurrentYear: false,
        });
      }
      setErrors({ year: "" });
      setShowConfirmModal(false);
      setPendingSave(null);
    }
  }, [selectedItem, isOpen]);

  // VALIDATION: Year must be 4 digits, numbers only, between 2000-2100
  const validateYear = (yearValue) => {
    if (!yearValue || yearValue.trim() === "") {
      return "Year is required";
    }

    // Check if contains only numbers
    if (!/^\d+$/.test(yearValue)) {
      return "Year must contain only numbers";
    }

    const year = parseInt(yearValue, 10);

    // Check if exactly 4 digits
    if (yearValue.length !== 4) {
      return "Year must be exactly 4 digits";
    }

    // Check range
    if (year < 2000 || year > 2100) {
      return "Year must be between 2000 and 2100";
    }

    // Check for duplicate year (for new entries or when changing year)
    const yearExists = existingData?.some(item =>
      item.year === year && (!selectedItem || item.id !== selectedItem.id)
    );

    if (yearExists) {
      return "This year already exists in the calendar";
    }

    return "";
  };

  const handleYearChange = (e) => {
    let value = e.target.value;

    // Remove any non-numeric characters
    value = value.replace(/[^\d]/g, '');

    // Limit to 4 digits
    if (value.length > 4) {
      value = value.slice(0, 4);
    }

    setForm({ ...form, year: value });

    // Validate on change
    const error = validateYear(value);
    setErrors({ ...errors, year: error });
  };

  const handleCurrentYearChange = (val) => {
    // If trying to set as current year, show confirmation
    if (val && !form.isCurrentYear) {
      const yearNum = parseInt(form.year, 10);
      const currentYearNum = new Date().getFullYear();

      // Show confirmation for setting current year
      setShowConfirmModal(true);
      setPendingSave({
        action: 'setCurrentYear',
        value: val,
        isPastYear: yearNum < currentYearNum
      });
    } else {
      setForm({ ...form, isCurrentYear: val });
    }
  };

  const handleSaveClick = () => {
    // Validate before saving
    const yearError = validateYear(form.year);
    if (yearError) {
      setErrors({ ...errors, year: yearError });
      return;
    }

    // If setting as current year, show confirmation
    if (form.isCurrentYear && !selectedItem?.isCurrentYear) {
      const yearNum = parseInt(form.year, 10);
      const currentYearNum = new Date().getFullYear();

      setShowConfirmModal(true);
      setPendingSave({
        action: 'save',
        isPastYear: yearNum < currentYearNum
      });
      return;
    }

    // If updating from current year to not current, show warning
    if (selectedItem?.isCurrentYear && !form.isCurrentYear) {
      setShowConfirmModal(true);
      setPendingSave({
        action: 'unsetCurrentYear',
        year: selectedItem.year
      });
      return;
    }

    // Proceed with save
    onSave(form);
  };

  // MODIFIED: Simplified to use direct boolean values now that Table bug is fixed
  const handleConfirmAction = () => {
    if (!pendingSave) {
      setShowConfirmModal(false);
      return;
    }

    switch (pendingSave.action) {
      case 'setCurrentYear':
        setForm({ ...form, isCurrentYear: pendingSave.value });
        break;
      case 'save':
        setShowConfirmModal(false);
        // REPLACED: No longer checking for "true" strings as inputs are now clean booleans
        onSave({
          ...form,
          isActive: Boolean(form.isActive),
          isCurrentYear: true
        });
        break;
      case 'unsetCurrentYear':
        setShowConfirmModal(false);
        // REPLACED: No longer checking for "true" strings
        onSave({
          ...form,
          isActive: Boolean(form.isActive),
          isCurrentYear: false
        });
        break;
      default:
        break;
    }

    setPendingSave(null);
  };

  const getConfirmModalMessage = () => {
    if (pendingSave?.action === 'setCurrentYear') {
      const yearMsg = pendingSave.isPastYear
        ? `⚠️ Warning: ${form.year} is in the past.\n\n`
        : '';
      return `${yearMsg}Are you sure you want to set ${form.year} as the current calendar year? This will affect leave calculations and other time-based features across the system.`;
    }

    if (pendingSave?.action === 'save' && pendingSave?.isPastYear) {
      return `⚠️ Warning: You are setting ${form.year} as the current year, which is in the past.\n\nAre you sure you want to continue?`;
    }

    if (pendingSave?.action === 'unsetCurrentYear') {
      return `⚠️ Warning: You are removing "${pendingSave.year}" as the current year.\n\nMake sure you have set another year as current before proceeding. Are you sure you want to continue?`;
    }

    return "Are you sure you want to proceed?";
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto"
        style={{ marginTop: "0px" }}>
        <div className="bg-white w-full md:w-[500px] rounded-lg shadow-xl flex flex-col">
          <div className="flex justify-between items-center px-6 py-4 border-b">
            <h2 className="text-xl font-bold">{selectedItem ? "Edit" : "Add"} Calendar Year</h2>
            <button onClick={onClose} className="bg-transparent border-none cursor-pointer"><CloseIcon /></button>
          </div>

          <div className="p-6 space-y-4">
            <Input
              label="Year"
              placeholder="Enter year (e.g., 2025)"
              value={form.year}
              onChange={handleYearChange}
              isRequired
              error={!!errors.year}
              errorMessage={errors.year}
              type="text"
              inputMode="numeric"
              pattern="\d*"
            />

            <div className="grid grid-cols-2 gap-4">
              <CheckInput
                name="Active"
                value={form.isActive}
                onChange={(val) => setForm({ ...form, isActive: val })}
              />

              <CheckInput
                name="Set as Current Year"
                value={form.isCurrentYear}
                onChange={handleCurrentYearChange}
                disabled={selectedItem && selectedItem.isCurrentYear && !form.isCurrentYear}
              />
            </div>

            {form.isCurrentYear && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Setting this as the current year will affect leave calculations and
                  other time-based features across the system.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 p-5 border-t">
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded border-none cursor-pointer">Cancel</button>
            <button onClick={handleSaveClick} className="px-6 py-2 bg-blue-600 text-white rounded border-none cursor-pointer">Save</button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingSave(null);
        }}
        onConfirm={handleConfirmAction}
        title="Confirm Action"
        message={getConfirmModalMessage()}
        confirmText="Yes, Proceed"
        cancelText="Cancel"
        type="warning"
      />
    </>
  );
};

export default HRCalendarAddEditModal;